import type { RealtimeEngineConfig } from '../../services/api';
import { normalizeRealtimeEngineConfig } from '../realtime/config';
import type {
  CreatePromptRequest,
  GeneratedPromptDraft,
  Prompt,
  PromptExecutionConfig,
  PromptSelectionFilter,
} from '../../services/api';
import {
  DEFAULT_PROMPT_EXECUTION_CONFIG,
  normalizePromptExecutionConfig,
  normalizePromptSelectionFilter,
} from '../workflows/config';
import {
  createPromptSchemaField,
  getBuilderSchemaAllowsAdditionalProperties,
  getPromptSchemaFieldValidationError,
  IMPLICIT_OPEN_ADDITIONAL_PROPERTIES,
  mergePromptSchemaWithOriginal,
  PromptSchemaAdditionalProperties,
  PromptSchemaFieldAdvancedOptions,
  PromptSchemaFieldDefinition,
  PromptSchemaFieldType,
  promptSchemaFieldsToJsonSchema,
  sanitizePromptSemanticIndexingDisabledFields,
  schemaToBuilderPromptSchemaFields,
  validatePromptJsonSchema,
} from '../../components/prompt-schema/schemaFields';

export type SchemaEditorMode = 'simple' | 'advanced' | 'raw';

export interface PromptCreationDraft {
  isRealtime?: boolean;
  realtimeConfig?: RealtimeEngineConfig | null;
  name: string;
  description: string;
  promptText: string;
  fields: PromptSchemaFieldDefinition[];
  schemaAllowsAdditionalProperties: PromptSchemaAdditionalProperties;
  videoLevelEnabled: boolean;
  videoLevelInstructions: string;
  videoLevelFields: PromptSchemaFieldDefinition[];
  videoLevelSchemaAllowsAdditionalProperties: PromptSchemaAdditionalProperties;
  segmentSchemaMode: SchemaEditorMode;
  segmentRawSchemaText: string;
  videoLevelSchemaMode: SchemaEditorMode;
  videoLevelRawSchemaText: string;
  semanticIndexingDisabledSegmentFields: string[];
  semanticIndexingDisabledVideoLevelFields: string[];
  executionConfig: PromptExecutionConfig;
  selectionFilter: PromptSelectionFilter | null;
  assistantPrompt: string;
  assistantPanelOpen: boolean;
}

export const formatPromptDraftSchemaJson = (schema?: Record<string, any> | null): string => (
  JSON.stringify(schema ?? { type: 'object', properties: {} }, null, 2)
);

export const createDefaultPromptCreationDraft = (): PromptCreationDraft => ({
  isRealtime: false,
  realtimeConfig: null,
  name: '',
  description: '',
  promptText: '',
  fields: [],
  schemaAllowsAdditionalProperties: false,
  videoLevelEnabled: false,
  videoLevelInstructions: '',
  videoLevelFields: [],
  videoLevelSchemaAllowsAdditionalProperties: false,
  segmentSchemaMode: 'simple',
  segmentRawSchemaText: formatPromptDraftSchemaJson(),
  videoLevelSchemaMode: 'simple',
  videoLevelRawSchemaText: formatPromptDraftSchemaJson(),
  semanticIndexingDisabledSegmentFields: [],
  semanticIndexingDisabledVideoLevelFields: [],
  executionConfig: { ...DEFAULT_PROMPT_EXECUTION_CONFIG },
  selectionFilter: null,
  assistantPrompt: '',
  assistantPanelOpen: true,
});

const VALID_SCHEMA_EDITOR_MODES = new Set<SchemaEditorMode>(['simple', 'advanced', 'raw']);
const VALID_PROMPT_FIELD_TYPES = new Set<PromptSchemaFieldType>([
  'any',
  'string',
  'integer',
  'number',
  'boolean',
  'enum',
  'object',
  'array_string',
  'array_enum',
  'array_integer',
  'array_number',
  'array_boolean',
  'array_object',
]);

const isPlainObject = (value: unknown): value is Record<string, any> => (
  Boolean(value) && typeof value === 'object' && !Array.isArray(value)
);

const cloneJsonLikeValue = (value: unknown): unknown => {
  if (value === undefined) {
    return undefined;
  }
  try {
    return JSON.parse(JSON.stringify(value));
  } catch {
    return undefined;
  }
};

const normalizeStringArray = (value: unknown): string[] => (
  Array.isArray(value)
    ? value
        .filter((entry): entry is string => typeof entry === 'string')
        .map((entry) => entry.trim())
        .filter(Boolean)
    : []
);

const normalizeSchemaEditorMode = (value: unknown): SchemaEditorMode => (
  typeof value === 'string' && VALID_SCHEMA_EDITOR_MODES.has(value as SchemaEditorMode)
    ? value as SchemaEditorMode
    : 'simple'
);

const normalizeAdditionalProperties = (
  value: unknown,
  fallback: PromptSchemaAdditionalProperties = false,
): PromptSchemaAdditionalProperties => {
  if (value === IMPLICIT_OPEN_ADDITIONAL_PROPERTIES) {
    return value;
  }
  if (typeof value === 'boolean') {
    return value;
  }
  if (isPlainObject(value)) {
    return cloneJsonLikeValue(value) as Record<string, any>;
  }
  return fallback;
};

const normalizeAdvancedOptions = (value: unknown): PromptSchemaFieldAdvancedOptions | undefined => {
  if (!isPlainObject(value)) {
    return undefined;
  }

  const nextValue: PromptSchemaFieldAdvancedOptions = {};
  const copyScalarOption = <K extends keyof PromptSchemaFieldAdvancedOptions>(
    key: K,
  ): void => {
    const optionValue = value[key];
    if (typeof optionValue === 'number' || typeof optionValue === 'string') {
      (nextValue as Record<string, string | number | boolean | undefined>)[key] = optionValue;
    }
  };

  copyScalarOption('minLength');
  copyScalarOption('maxLength');
  copyScalarOption('pattern');
  copyScalarOption('format');
  copyScalarOption('minimum');
  copyScalarOption('maximum');
  copyScalarOption('minItems');
  copyScalarOption('maxItems');
  copyScalarOption('minProperties');
  copyScalarOption('maxProperties');

  if (typeof value.uniqueItems === 'boolean') {
    nextValue.uniqueItems = value.uniqueItems;
  }

  return Object.keys(nextValue).length > 0 ? nextValue : undefined;
};

const normalizePromptFieldType = (value: unknown): PromptSchemaFieldType => (
  typeof value === 'string' && VALID_PROMPT_FIELD_TYPES.has(value as PromptSchemaFieldType)
    ? value as PromptSchemaFieldType
    : 'string'
);

const normalizePromptFieldDefinition = (
  value: unknown,
): PromptSchemaFieldDefinition => {
  const rawField = isPlainObject(value) ? value : {};
  return createPromptSchemaField({
    id: typeof rawField.id === 'string' && rawField.id.trim() ? rawField.id.trim() : undefined,
    name: typeof rawField.name === 'string' ? rawField.name : '',
    type: normalizePromptFieldType(rawField.type),
    description: typeof rawField.description === 'string' ? rawField.description : '',
    required: rawField.required === true,
    allowAdditionalProperties: normalizeAdditionalProperties(rawField.allowAdditionalProperties, false),
    defaultValue: cloneJsonLikeValue(rawField.defaultValue),
    enumValues: Array.isArray(rawField.enumValues)
      ? rawField.enumValues.filter((entry): entry is string | number | boolean => (
          typeof entry === 'string' || typeof entry === 'number' || typeof entry === 'boolean'
        ))
      : [],
    advancedOptions: normalizeAdvancedOptions(rawField.advancedOptions),
    nestedFields: Array.isArray(rawField.nestedFields)
      ? rawField.nestedFields.map((nestedField) => normalizePromptFieldDefinition(nestedField))
      : [],
    preserveAsHiddenRequired: rawField.preserveAsHiddenRequired === true,
    sourceSchema: isPlainObject(rawField.sourceSchema)
      ? cloneJsonLikeValue(rawField.sourceSchema) as Record<string, any>
      : undefined,
  });
};

const normalizePromptFieldDefinitions = (value: unknown): PromptSchemaFieldDefinition[] => (
  Array.isArray(value)
    ? value.map((field) => normalizePromptFieldDefinition(field))
    : []
);

const resolveRawSchemaText = (
  rawText: unknown,
  fields: PromptSchemaFieldDefinition[],
  allowAdditionalProperties: PromptSchemaAdditionalProperties,
): string => (
  typeof rawText === 'string' && rawText.trim()
    ? rawText
    : formatPromptDraftSchemaJson(
        promptSchemaFieldsToJsonSchema(fields, {
          allowAdditionalProperties,
        }),
      )
);

export const normalizePromptCreationDraft = (
  value: unknown,
): PromptCreationDraft | null => {
  if (!isPlainObject(value)) {
    return null;
  }

  const segmentFields = normalizePromptFieldDefinitions(value.fields);
  const videoLevelFields = normalizePromptFieldDefinitions(value.videoLevelFields);
  const schemaAllowsAdditionalProperties = normalizeAdditionalProperties(
    value.schemaAllowsAdditionalProperties,
    false,
  );
  const videoLevelSchemaAllowsAdditionalProperties = normalizeAdditionalProperties(
    value.videoLevelSchemaAllowsAdditionalProperties,
    false,
  );

  return {
    ...createDefaultPromptCreationDraft(),
    isRealtime: value.isRealtime === true,
    realtimeConfig: value.isRealtime === true ? normalizeRealtimeEngineConfig(value.realtimeConfig as RealtimeEngineConfig) : null,
    name: typeof value.name === 'string' ? value.name : '',
    description: typeof value.description === 'string' ? value.description : '',
    promptText: typeof value.promptText === 'string' ? value.promptText : '',
    fields: segmentFields,
    schemaAllowsAdditionalProperties,
    videoLevelEnabled: value.videoLevelEnabled === true,
    videoLevelInstructions: typeof value.videoLevelInstructions === 'string'
      ? value.videoLevelInstructions
      : '',
    videoLevelFields,
    videoLevelSchemaAllowsAdditionalProperties,
    segmentSchemaMode: normalizeSchemaEditorMode(value.segmentSchemaMode),
    segmentRawSchemaText: resolveRawSchemaText(
      value.segmentRawSchemaText,
      segmentFields,
      schemaAllowsAdditionalProperties,
    ),
    videoLevelSchemaMode: normalizeSchemaEditorMode(value.videoLevelSchemaMode),
    videoLevelRawSchemaText: resolveRawSchemaText(
      value.videoLevelRawSchemaText,
      videoLevelFields,
      videoLevelSchemaAllowsAdditionalProperties,
    ),
    semanticIndexingDisabledSegmentFields: normalizeStringArray(
      value.semanticIndexingDisabledSegmentFields,
    ),
    semanticIndexingDisabledVideoLevelFields: normalizeStringArray(
      value.semanticIndexingDisabledVideoLevelFields,
    ),
    executionConfig: normalizePromptExecutionConfig({

      ...(isPlainObject(value.executionConfig) ? value.executionConfig : {}),
    }),
    selectionFilter: normalizePromptSelectionFilter(value.selectionFilter),
    assistantPrompt: typeof value.assistantPrompt === 'string' ? value.assistantPrompt : '',
    assistantPanelOpen: typeof value.assistantPanelOpen === 'boolean' ? value.assistantPanelOpen : true,
  };
};

type PromptDraftConfig = Pick<
  Prompt,
  'name' | 'description' | 'prompt_text' | 'json_schema' | 'video_level' | 'semantic_indexing' | 'execution_config' | 'selection_filter' | 'is_realtime' | 'realtime_config'
>;

export const buildPromptCreationDraftFromPromptConfig = (
  config: PromptDraftConfig,
  overrides: Partial<Pick<PromptCreationDraft, 'assistantPrompt' | 'assistantPanelOpen'>> = {},
): PromptCreationDraft => ({
  isRealtime: config.is_realtime === true,
  realtimeConfig: config.is_realtime ? normalizeRealtimeEngineConfig(config.realtime_config) : null,
  name: config.name || '',
  description: config.description || '',
  promptText: config.prompt_text || '',
  fields: schemaToBuilderPromptSchemaFields(config.json_schema || { type: 'object', properties: {} }),
  schemaAllowsAdditionalProperties: getBuilderSchemaAllowsAdditionalProperties(
    config.json_schema || { type: 'object', properties: {} },
  ),
  videoLevelEnabled: Boolean(config.video_level),
  videoLevelInstructions: config.video_level?.instructions_text || '',
  videoLevelFields: config.video_level
    ? schemaToBuilderPromptSchemaFields(config.video_level.json_schema)
    : [],
  videoLevelSchemaAllowsAdditionalProperties: config.video_level
    ? getBuilderSchemaAllowsAdditionalProperties(config.video_level.json_schema)
    : false,
  segmentSchemaMode: 'simple',
  segmentRawSchemaText: formatPromptDraftSchemaJson(config.json_schema),
  videoLevelSchemaMode: 'simple',
  videoLevelRawSchemaText: formatPromptDraftSchemaJson(config.video_level?.json_schema),
  semanticIndexingDisabledSegmentFields: config.semantic_indexing?.disabled_segment_fields || [],
  semanticIndexingDisabledVideoLevelFields: config.semantic_indexing?.disabled_video_level_fields || [],
  executionConfig: normalizePromptExecutionConfig(config.execution_config),
  selectionFilter: normalizePromptSelectionFilter(config.selection_filter),
  assistantPrompt: overrides.assistantPrompt || '',
  assistantPanelOpen: overrides.assistantPanelOpen ?? true,
});

export const buildPromptCreationDraftFromAssistantResult = (
  generated: GeneratedPromptDraft,
  settings: Pick<PromptCreationDraft, 'isRealtime' | 'realtimeConfig' | 'executionConfig'>,
  instruction: string,
): PromptCreationDraft => buildPromptCreationDraftFromPromptConfig({
  ...generated,
  ...(settings.isRealtime ? {
    is_realtime: true,
    realtime_config: settings.realtimeConfig,
    video_level: null,
    selection_filter: generated.selection_filter?.segment
      ? { segment: generated.selection_filter.segment, level_logic: 'and' as const }
      : null,
  } : {}),
  execution_config: settings.executionConfig,
}, { assistantPrompt: instruction, assistantPanelOpen: true });

const parseRawPromptSchema = (
  schemaText: string,
  label: 'segment' | 'video-level',
): Record<string, any> => {
  let parsed: unknown;
  try {
    parsed = JSON.parse(schemaText);
  } catch {
    throw new Error(`Raw ${label} schema must be valid JSON.`);
  }

  if (!isPlainObject(parsed)) {
    throw new Error(`Raw ${label} schema must be a JSON object.`);
  }

  const validationError = validatePromptJsonSchema(parsed);
  if (validationError) {
    throw new Error(`Raw ${label} schema is invalid: ${validationError}`);
  }

  return parsed;
};

const parseOriginalBuilderSchema = (schemaText: string): Record<string, any> | null => {
  try {
    const parsed = JSON.parse(schemaText);
    return isPlainObject(parsed) ? parsed : null;
  } catch {
    // Raw text is not authoritative while the builder is active. Ignore a
    // stale editor buffer instead of letting it replace valid builder state.
    return null;
  }
};

const buildBuilderPromptSchema = (
  fields: PromptSchemaFieldDefinition[],
  allowAdditionalProperties: PromptSchemaAdditionalProperties,
  originalSchemaText: string,
  fieldLabel: string,
  schemaLabel: 'Segment' | 'Video-level',
): Record<string, any> => {
  const fieldValidationError = getPromptSchemaFieldValidationError(fields, fieldLabel, {
    allowAdditionalProperties,
  });
  if (fieldValidationError) {
    throw new Error(`${schemaLabel} schema is invalid: ${fieldValidationError}`);
  }

  const schema = mergePromptSchemaWithOriginal(
    promptSchemaFieldsToJsonSchema(fields, { allowAdditionalProperties }),
    parseOriginalBuilderSchema(originalSchemaText),
  );
  const schemaValidationError = validatePromptJsonSchema(schema);
  if (schemaValidationError) {
    throw new Error(`${schemaLabel} schema is invalid: ${schemaValidationError}`);
  }

  return schema;
};

/**
 * Builds the API creation contract from the persisted extraction-engine draft.
 * Raw schemas are authoritative only while their editor is in raw mode;
 * otherwise the builder fields are authoritative and the raw text is used
 * solely to preserve valid schema keywords that the visual builder does not
 * edit directly.
 */
export const buildCreatePromptRequestFromPromptCreationDraft = (
  value: PromptCreationDraft,
): CreatePromptRequest => {
  const draft = normalizePromptCreationDraft(value);
  if (!draft) {
    throw new Error('Extraction engine draft is invalid.');
  }

  const name = draft.name.trim();
  if (!name) {
    throw new Error('Extraction engine name is required.');
  }
  if (name.length < 3) {
    throw new Error('Extraction engine name must be at least 3 characters.');
  }

  const promptText = draft.promptText.trim();
  if (!promptText) {
    throw new Error('Extraction engine text is required.');
  }
  if (promptText.length < 10) {
    throw new Error('Extraction engine text must be at least 10 characters.');
  }

  const jsonSchema = draft.segmentSchemaMode === 'raw'
    ? parseRawPromptSchema(draft.segmentRawSchemaText, 'segment')
    : buildBuilderPromptSchema(
        draft.fields,
        draft.schemaAllowsAdditionalProperties,
        draft.segmentRawSchemaText,
        'field',
        'Segment',
      );
  const segmentFields = draft.segmentSchemaMode === 'raw'
    ? schemaToBuilderPromptSchemaFields(jsonSchema)
    : draft.fields;
  let videoLevel: CreatePromptRequest['video_level'] = null;
  let videoLevelFields: PromptSchemaFieldDefinition[] = [];
  if (draft.videoLevelEnabled) {
    const instructions = draft.videoLevelInstructions.trim();
    if (!instructions) {
      throw new Error('Video-level instructions are required.');
    }

    const config = normalizePromptExecutionConfig(draft.executionConfig);
    if (config.video_segmentation_type === 'fixed' || config.audio_segmentation_type === 'fixed') {
      throw new Error('Video-level fields require content-aware segmentation.');
    }

    const videoLevelSchema = draft.videoLevelSchemaMode === 'raw'
      ? parseRawPromptSchema(draft.videoLevelRawSchemaText, 'video-level')
      : buildBuilderPromptSchema(
          draft.videoLevelFields,
          draft.videoLevelSchemaAllowsAdditionalProperties,
          draft.videoLevelRawSchemaText,
          'video-level output field',
          'Video-level',
        );
    videoLevelFields = draft.videoLevelSchemaMode === 'raw'
      ? schemaToBuilderPromptSchemaFields(videoLevelSchema)
      : draft.videoLevelFields;
    videoLevel = {
      instructions_text: instructions,
      json_schema: videoLevelSchema,
    };
  }

  return {
    ...(draft.isRealtime ? { is_realtime: true, realtime_config: normalizeRealtimeEngineConfig(draft.realtimeConfig) } : {}),
    name,
    description: draft.description.trim(),
    prompt_text: promptText,
    json_schema: jsonSchema,
    video_level: videoLevel,
    semantic_indexing: {
      disabled_segment_fields: sanitizePromptSemanticIndexingDisabledFields(
        draft.semanticIndexingDisabledSegmentFields,
        segmentFields,
      ),
      disabled_video_level_fields: draft.videoLevelEnabled
        ? sanitizePromptSemanticIndexingDisabledFields(
            draft.semanticIndexingDisabledVideoLevelFields,
            videoLevelFields,
          )
        : [],
    },
    execution_config: normalizePromptExecutionConfig(draft.executionConfig),
    selection_filter: normalizePromptSelectionFilter(draft.selectionFilter),
  };
};
